import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Star, QrCode, Lightning, Microphone, FilmStrip, DeviceMobile, Camera, Envelope, Check } from '@phosphor-icons/react';
import { EventDropNav, EventDropWatermark } from '../components/EventDropLogo';

const LandingV2 = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white overflow-hidden" style={{ fontFamily: "'Inter', sans-serif", background: '#15161B' }} data-testid="landing-v2">

      {/* Animated confetti particles */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {Array.from({ length: 25 }).map((_, i) => (
          <div key={i} className="absolute" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${6 + Math.random() * 10}px`,
            height: `${6 + Math.random() * 10}px`,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            background: ['#FF0099', '#00FFDD', '#FFCC00', '#B89762', '#C29D52', '#FF6B6B'][i % 6],
            opacity: 0.15 + Math.random() * 0.15,
            animation: `confettiFall ${8 + Math.random() * 12}s linear infinite`,
            animationDelay: `${Math.random() * 10}s`,
          }} />
        ))}
      </div>

      {/* Background gradient blobs */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(108,92,231,0.15), transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(62,198,231,0.1), transparent 70%)' }} />
      </div>

      {/* NAV */}
      <nav className="relative z-20 max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <EventDropNav onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
        <div className="flex items-center gap-3">
          <button className="px-5 py-2 rounded-xl text-sm font-medium text-white/70 border border-white/10 hover:bg-[#15161B] transition-colors">Log in</button>
          <button onClick={() => navigate('/create')} className="px-5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #C29D52, #32C0E0)' }}>
            Try demo
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              Create an event page people actually get
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #B89762, #C29D52)' }}> excited about</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="text-lg mb-8" style={{ color: '#F4E9D2' }}>
              Build, share, and fill your event in minutes — without boring tools.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="flex flex-wrap gap-3 mb-4">
              <button onClick={() => navigate('/create')} className="px-7 py-4 rounded-2xl text-base font-bold text-white flex items-center gap-2 transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #B89762, #5A4AE3)', boxShadow: '0 8px 30px rgba(108,92,231,0.3)' }}>
                Create your event free
              </button>
              <button className="px-7 py-4 rounded-2xl text-base font-medium border border-white/15 hover:bg-[#15161B] transition-colors flex items-center gap-2" style={{ color: '#F4E9D2' }}>
                <Play size={16} weight="fill" /> See a live example
              </button>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="text-sm" style={{ color: 'rgba(244,233,210,0.55)' }}>
              No signup required · Takes 60 seconds
            </motion.p>
          </div>

          {/* Right — Event page mockup */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 0.6 }}>
            <div className="rounded-2xl overflow-hidden" style={{ background: '#15161B', boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 40px rgba(108,92,231,0.15)' }}>
              {/* Browser dots */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                <div className="w-3 h-3 rounded-full bg-green-400/60" />
              </div>
              {/* Mock event page */}
              <div className="p-6" style={{ background: 'linear-gradient(180deg, #2D1B69, #15161B)' }}>
                <h3 className="text-xl font-bold mb-1">Alex's Rooftop Party</h3>
                <p className="text-sm mb-4" style={{ color: 'rgba(244,233,210,0.55)' }}>Saturday, June 12 · 7:00 PM · Brooklyn, NY</p>
                <div className="rounded-xl h-40 mb-4 flex items-end" style={{ background: 'linear-gradient(180deg, #4A2580, #B89762)', overflow: 'hidden' }}>
                  <div className="w-full p-4 text-center" style={{ background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))' }}>
                    <div className="flex justify-center gap-1 mb-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="w-8 h-8 rounded-full border-2 border-white/30" style={{ background: ['#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF6B6B'][i], marginLeft: i > 0 ? '-8px' : 0 }} />
                      ))}
                    </div>
                  </div>
                </div>
                <button className="w-full py-3.5 rounded-2xl font-bold text-white text-center" style={{ background: 'linear-gradient(135deg, #B89762, #A033D8)' }}>
                  I'm Going
                </button>
                <div className="flex items-center justify-center gap-3 mt-3" style={{ color: 'rgba(244,233,210,0.55)' }}>
                  <span className="text-xs">38 Going</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="relative z-10 py-16 border-y" style={{ borderColor: 'rgba(255,255,255,0.05)', background: 'rgba(15,18,34,0.8)' }}>
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-center font-bold text-lg mb-8">Loved by 1,000+ event organisers</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { quote: '"Took me 2 minutes. Insane."', name: 'Sarah M.' },
              { quote: '"Way better than anything I\'ve used before."', name: 'Alex R.' },
              { quote: '"People actually showed up!"', name: 'James K.' },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ background: '#15161B', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(s => <Star key={s} size={14} weight="fill" className="text-yellow-400" />)}</div>
                <p className="text-sm mb-3" style={{ color: '#F4E9D2' }}>{t.quote}</p>
                <p className="text-xs" style={{ color: 'rgba(244,233,210,0.55)' }}>{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEMO SHOWCASE */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-8">See what your event could look like</h2>
          <div className="rounded-2xl overflow-hidden" style={{ background: '#15161B', boxShadow: '0 30px 80px rgba(0,0,0,0.4)' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
              <div className="w-3 h-3 rounded-full bg-red-400/60" /><div className="w-3 h-3 rounded-full bg-yellow-400/60" /><div className="w-3 h-3 rounded-full bg-green-400/60" />
            </div>
            <div className="p-8 text-center" style={{ background: 'linear-gradient(135deg, #FF6B6B40, #C29D5220, #B8976220)', minHeight: 300 }}>
              <h3 className="text-3xl font-bold mb-2">Summer Beach Bash</h3>
              <p className="mb-6" style={{ color: '#F4E9D2' }}>August 20th, 2026 · Malibu, CA</p>
              <button className="px-8 py-3.5 rounded-2xl font-bold text-white" style={{ background: 'linear-gradient(135deg, #C29D52, #B89762)' }}>
                Open a real event page →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-10">How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { num: '01', title: 'Create your event', desc: 'Start with just a name and date' },
              { num: '02', title: 'Share your QR code', desc: 'Guests scan and upload instantly' },
              { num: '03', title: 'Watch moments come to life', desc: 'Gallery fills up in real-time' },
            ].map((step, i) => (
              <motion.div key={i} whileHover={{ y: -8 }} className="rounded-2xl p-6 text-center" style={{ background: '#15161B', border: '1px solid rgba(255,255,255,0.06)' }}>
                <span className="text-4xl font-black" style={{ color: '#B8976220' }}>{step.num}</span>
                <h3 className="text-lg font-bold mt-2 mb-2">{step.title}</h3>
                <p className="text-sm" style={{ color: 'rgba(244,233,210,0.55)' }}>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-black text-center mb-4">Build your perfect event</h2>
          <p className="text-center mb-10" style={{ color: 'rgba(244,233,210,0.55)' }}>Mix and match features</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Challenge Mode', price: '£29', color: '#FF6B6B', desc: 'Live challenges + leaderboards', tag: 'Most Popular' },
              { title: 'QR Capture', price: '£29', color: '#C29D52', desc: 'Guests upload via QR instantly' },
              { title: 'Invites', price: '£29', color: '#B89762', desc: 'Send invites & track RSVPs' },
              { title: 'Guestbook', price: '£39', color: '#B89762', desc: 'Video & audio messages' },
              { title: 'Event Film', price: '£29', color: '#FF0099', desc: 'Cinematic film from all content' },
              { title: 'Highlight Reel', price: '£12', color: '#FFCC00', desc: '30s vertical for social' },
            ].map((f, i) => (
              <motion.div key={i} whileHover={{ y: -6 }} className="rounded-2xl p-6 relative" style={{ background: '#15161B', border: '1px solid rgba(255,255,255,0.06)' }}>
                {f.tag && <span className="absolute top-4 right-4 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${f.color}20`, color: f.color }}>{f.tag}</span>}
                <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center" style={{ background: `${f.color}20` }}>
                  <div className="w-3 h-3 rounded-full" style={{ background: f.color }} />
                </div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">{f.title}</h3>
                  <span className="font-black" style={{ color: f.color }}>{f.price}</span>
                </div>
                <p className="text-sm" style={{ color: 'rgba(244,233,210,0.55)' }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center mt-6 text-sm" style={{ color: '#B89762' }}>Save 10% when you add multiple features</p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 py-24 px-6 text-center">
        <h2 className="text-3xl font-black mb-4">Ready to create something unforgettable?</h2>
        <p className="mb-8" style={{ color: 'rgba(244,233,210,0.55)' }}>Your event is 60 seconds away</p>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('/create')}
          className="px-10 py-5 rounded-2xl text-lg font-bold text-white inline-flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', boxShadow: '0 8px 40px rgba(108,92,231,0.3)' }}>
          Start creating now <ArrowRight size={20} weight="bold" />
        </motion.button>
      </section>

      {/* FOOTER */}
      <footer className="relative z-10 py-10 px-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <EventDropWatermark />
          <p className="text-sm" style={{ color: 'rgba(244,233,210,0.55)' }}>&copy; 2026 EventDrop</p>
        </div>
      </footer>

      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-10vh) rotate(0deg); }
          100% { transform: translateY(110vh) rotate(720deg); }
        }
      `}</style>
    </div>
  );
};

export default LandingV2;
